// fatdisk.js — FAT12 filesystem layer for the 磁盘文件 drawer.
//
// Wraps js-fatfs (ChaN's FatFs compiled to wasm) behind a small API. Every
// Disk operates on a PRIVATE copy of the image bytes: callers mount the
// exported floppy, mutate, then push disk.image() back via
// machine.swapFloppy(). Nothing here touches the emulator.
//
// Filenames: the MS2401 stores GBK bytes in 8.3 directory entries. FatFs is
// created with codepage 936 and its Unicode API converts GBK short names
// to/from JS strings transparently (verified against the stock floppy.img:
// 世界语 etc. list and rename correctly). We never encode GBK ourselves.
import * as FatFs from 'js-fatfs';

const SECTOR = 512;

const FR_MSG = {
  [FatFs.FR_DISK_ERR]: '磁盘读写错误',
  [FatFs.FR_NO_FILE]: '文件不存在',
  [FatFs.FR_NO_PATH]: '路径不存在',
  [FatFs.FR_INVALID_NAME]: '文件名无效',
  [FatFs.FR_DENIED]: '操作被拒绝（目录非空或磁盘已满）',
  [FatFs.FR_EXIST]: '同名文件已存在',
  [FatFs.FR_WRITE_PROTECTED]: '磁盘写保护',
  [FatFs.FR_NO_FILESYSTEM]: '无法识别的磁盘格式',
};

export class FatError extends Error {
  constructor(rc, op) {
    super(`${op}: ${FR_MSG[rc] || `FatFs 错误 ${rc}`}`);
    this.rc = rc;
  }
}

function check(rc, op) { if (rc !== FatFs.FR_OK) throw new FatError(rc, op); }

// GBK byte length: a CJK char is 2 bytes in codepage 936, ASCII is 1. Shared by
// suggestName and the 8.3 guard so both count Chinese names identically.
const gbkLen = (s) => [...s].reduce((n, c) => n + (c.charCodeAt(0) > 0x7f ? 2 : 1), 0);
// FAT-invalid filename characters (the char class suggestName sanitizes away).
const FAT_BAD = /[\s+,;=[\]"*:<>?|/\\]/;

// WYSIWYG guard: image() scrubs VFAT long names, so any target name that only
// FatFs's numeric-tail SFN could satisfy would silently degrade once exported.
// Reject up front — throw FR_INVALID_NAME when the last path segment doesn't fit
// 8.3 in GBK bytes (base > 8 or ext > 3) or contains a FAT-invalid char.
function ensure83(path, op) {
  const seg = path.replace(/\/+$/, '').split('/').pop() || '';
  const dot = seg.lastIndexOf('.');
  const base = dot > 0 ? seg.slice(0, dot) : seg;
  const ext = dot > 0 ? seg.slice(dot + 1) : '';
  // A '.' inside the base (multi-dot 'a.b.c', leading-dot '.gitignore') has no
  // 8.3 short form: FatFs would store it via LFN and the scrub would degrade it
  // to a numeric-tail SFN (~1) behind the user's back — reject it up front.
  if (gbkLen(base) > 8 || gbkLen(ext) > 3 || base.includes('.') ||
      FAT_BAD.test(base) || FAT_BAD.test(ext)) {
    throw new FatError(FatFs.FR_INVALID_NAME, op);
  }
}

// Scrub every VFAT long-name (attr 0x0F) directory entry from a FAT12 image by
// marking its first byte 0xE5 (deleted). Walks the root region plus every
// subdirectory's FAT12 cluster chain, parsing geometry straight from the BPB.
// This is a deliberate, narrow hand-rolled FAT walk (the library cannot disable
// FF_USE_LFN at runtime); it runs only on an exported COPY, never the live store.
function scrubLfn(img) {
  const u16 = (o) => img[o] | (img[o + 1] << 8);
  const bytesPerSec = u16(11);
  const secPerClus = img[13];
  const rsvdCnt = u16(14);
  const numFATs = img[16];
  const rootEntCnt = u16(17);
  const fatSz = u16(22);
  if (!bytesPerSec || !secPerClus) return; // not a sane BPB — leave untouched
  const clusBytes = secPerClus * bytesPerSec;
  const fatStart = rsvdCnt * bytesPerSec;
  const rootStart = (rsvdCnt + numFATs * fatSz) * bytesPerSec;
  const dataStart = rootStart + rootEntCnt * 32;

  const fatEntry = (n) => { // FAT12: 12-bit entry, packed 1.5 bytes each
    const off = fatStart + Math.floor((n * 3) / 2);
    const v = img[off] | (img[off + 1] << 8);
    return (n & 1) ? (v >> 4) : (v & 0xfff);
  };

  // Scrub one block of 32-byte entries; push subdir first-clusters onto `queue`.
  // Returns true at a 0x00 end-of-directory marker (caller stops the chain).
  const scrubBlock = (start, len, queue) => {
    for (let o = start; o + 32 <= start + len && o + 32 <= img.length; o += 32) {
      const first = img[o];
      if (first === 0x00) return true;                 // end of directory
      if (first === 0xE5) continue;                    // already deleted
      const attr = img[o + 11];
      if (attr === 0x0F) { img[o] = 0xE5; continue; }  // VFAT LFN: scrub it
      if ((attr & 0x10) && first !== 0x2E) {           // subdir (not '.'/'..')
        queue.push(u16(o + 26));                       // DIR_FstClusLO
      }
    }
    return false;
  };

  const queue = [];
  scrubBlock(rootStart, rootEntCnt * 32, queue);       // root is a flat region
  const seen = new Set();                              // guard corrupt/cyclic chains
  while (queue.length) {
    let clus = queue.shift();
    while (clus >= 2 && clus < 0xff8 && !seen.has(clus)) {
      seen.add(clus);
      if (scrubBlock(dataStart + (clus - 2) * clusBytes, clusBytes, queue)) break;
      clus = fatEntry(clus);
    }
  }
}

function decodeTime(fdate, ftime) {
  if (!fdate) return null;
  return new Date(1980 + (fdate >> 9), ((fdate >> 5) & 0xf) - 1, fdate & 0x1f,
    ftime >> 11, (ftime >> 5) & 0x3f, (ftime & 0x1f) * 2);
}

export async function mount(bytes) {
  const img = bytes.slice(); // private copy; the caller's buffer is never touched
  const diskio = {
    initialize: () => 0,
    status: () => 0,
    read: (ff, pdrv, buff, sector, count) => {
      ff.HEAPU8.set(img.subarray(sector * SECTOR, (sector + count) * SECTOR), buff);
      return FatFs.RES_OK;
    },
    write: (ff, pdrv, buff, sector, count) => {
      img.set(ff.HEAPU8.subarray(buff, buff + count * SECTOR), sector * SECTOR);
      return FatFs.RES_OK;
    },
    ioctl: (ff, pdrv, cmd, buff) => {
      switch (cmd) {
        case FatFs.CTRL_SYNC: return FatFs.RES_OK;
        case FatFs.GET_SECTOR_COUNT: ff.setValue(buff, Math.floor(img.length / SECTOR), 'i32'); return FatFs.RES_OK;
        case FatFs.GET_SECTOR_SIZE: ff.setValue(buff, SECTOR, 'i16'); return FatFs.RES_OK;
        case FatFs.GET_BLOCK_SIZE: ff.setValue(buff, 1, 'i32'); return FatFs.RES_OK;
        default: return FatFs.RES_PARERR;
      }
    },
  };
  // Each mount() spins up a fresh js-fatfs wasm instance. That's fine for a
  // floppy-sized image (mount/mutate/export is a brief, one-shot operation);
  // don't copy this pattern for larger volumes where instance churn would hurt.
  const ff = await FatFs.create({ diskio, codepage: 936 });
  const fsPtr = ff.malloc(FatFs.sizeof_FATFS);
  const rc = ff.f_mount(fsPtr, '', 1);
  if (rc !== FatFs.FR_OK) { ff.free(fsPtr); throw new FatError(rc, 'mount'); }
  return new Disk(ff, img);
}

class Disk {
  constructor(ff, img) { this.ff = ff; this._img = img; }

  // The (possibly mutated) image bytes, as a SCRUBBED COPY. FatFs flushes
  // FAT/dir state before every mutating call returns (f_close syncs;
  // rename/unlink/mkdir sync internally), so _img is always a consistent
  // filesystem. But js-fatfs is compiled with FF_USE_LFN, so a Chinese-named
  // create/rename also writes VFAT long-name entries (attr 0x0F) that the
  // MS2401's 1980s firmware must never see. We copy _img and scrub every 0x0F
  // entry to 0xE5 (deleted) on the COPY only — never the live _img: FatFs caches
  // directory sector windows, and scrubbing _img could be flushed back and
  // resurrect stale entries on a later write. The exported copy sidesteps that.
  image() {
    const out = this._img.slice();
    scrubLfn(out);
    return out;
  }

  list(path) {
    const ff = this.ff;
    const dp = ff.malloc(FatFs.sizeof_DIR);
    const rc = ff.f_opendir(dp, path);
    if (rc !== FatFs.FR_OK) { ff.free(dp); throw new FatError(rc, 'opendir'); }
    const fno = ff.malloc(FatFs.sizeof_FILINFO);
    const out = [];
    try {
      for (;;) {
        check(ff.f_readdir(dp, fno), 'readdir');
        const name = ff.FILINFO_fname(fno);
        if (!name) break;
        out.push({
          name,
          size: ff.FILINFO_fsize(fno),
          isDir: (ff.FILINFO_fattrib(fno) & FatFs.AM_DIR) !== 0,
          mtime: decodeTime(ff.FILINFO_fdate(fno), ff.FILINFO_ftime(fno)),
        });
      }
    } finally {
      ff.f_closedir(dp);
      ff.free(fno);
      ff.free(dp);
    }
    out.sort((a, b) => (b.isDir - a.isDir) || a.name.localeCompare(b.name, 'zh'));
    return out;
  }

  stat(path) {
    const ff = this.ff;
    const fno = ff.malloc(FatFs.sizeof_FILINFO);
    try {
      if (ff.f_stat(path, fno) !== FatFs.FR_OK) return null;
      return { isDir: (ff.FILINFO_fattrib(fno) & FatFs.AM_DIR) !== 0, size: ff.FILINFO_fsize(fno) };
    } finally { ff.free(fno); }
  }

  read(path) {
    const ff = this.ff;
    const fp = ff.malloc(FatFs.sizeof_FIL);
    const rc = ff.f_open(fp, path, FatFs.FA_READ);
    if (rc !== FatFs.FR_OK) { ff.free(fp); throw new FatError(rc, 'open'); }
    try {
      const size = ff.f_size(fp);
      const buf = ff.malloc(size || 1);
      const br = ff.malloc(4);
      try {
        check(ff.f_read(fp, buf, size, br), 'read');
        return ff.HEAPU8.slice(buf, buf + ff.getValue(br, 'i32'));
      } finally { ff.free(br); ff.free(buf); }
    } finally { ff.f_close(fp); ff.free(fp); }
  }

  write(path, data) {
    ensure83(path, 'create');
    const ff = this.ff;
    const fp = ff.malloc(FatFs.sizeof_FIL);
    const rc = ff.f_open(fp, path, FatFs.FA_WRITE | FatFs.FA_CREATE_ALWAYS);
    if (rc !== FatFs.FR_OK) { ff.free(fp); throw new FatError(rc, 'create'); }
    try {
      if (data.length > 0) {
        const buf = ff.malloc(data.length);
        const bw = ff.malloc(4);
        try {
          ff.HEAPU8.set(data, buf);
          check(ff.f_write(fp, buf, data.length, bw), 'write');
          if (ff.getValue(bw, 'i32') !== data.length) {
            // Disk full: f_write reports FR_OK with a short byte count.
            throw new FatError(FatFs.FR_DENIED, 'write');
          }
        } finally { ff.free(bw); ff.free(buf); }
      }
      check(ff.f_close(fp), 'write');
    } catch (e) {
      // A failed write must not leave partial garbage behind: by now
      // FA_CREATE_ALWAYS has created the file and a short write may have
      // consumed free clusters, so close the handle and unlink the partial
      // file before rethrowing — the filesystem is left exactly as it was.
      // (If path named a pre-existing file, its old contents were already
      // truncated by FA_CREATE_ALWAYS and are acceptably lost.)
      ff.f_close(fp);
      ff.f_unlink(path);
      throw e;
    } finally { ff.free(fp); }
  }

  remove(path, { recursive = false } = {}) {
    if (recursive) {
      const st = this.stat(path);
      if (st && st.isDir) {
        for (const e of this.list(path)) {
          this.remove(`${path.replace(/\/$/, '')}/${e.name}`, { recursive: true });
        }
      }
    }
    check(this.ff.f_unlink(path), 'delete');
  }

  rename(oldPath, newPath) {
    ensure83(newPath, 'rename');
    check(this.ff.f_rename(oldPath, newPath), 'rename');
  }

  mkdir(path) { ensure83(path, 'mkdir'); check(this.ff.f_mkdir(path), 'mkdir'); }

  freeSpace() {
    const ff = this.ff;
    const nclst = ff.malloc(4);
    const pfs = ff.malloc(4);
    try {
      check(ff.f_getfree('', nclst, pfs), 'getfree');
      const img = this._img;
      const u16 = (o) => img[o] | (img[o + 1] << 8);
      const bytesPerSec = u16(11);
      const spc = img[13];               // BPB_SecPerClus
      const rsvdCnt = u16(14);
      const numFATs = img[16];
      const rootEntCnt = u16(17);
      const fatSz = u16(22);
      const totSec = u16(19);            // BPB_TotSec16
      const rootSecs = Math.ceil((rootEntCnt * 32) / bytesPerSec);
      const dataSec = totSec - (rsvdCnt + numFATs * fatSz + rootSecs);
      const clusters = Math.floor(dataSec / spc);
      return {
        freeBytes: ff.getValue(nclst, 'i32') * spc * bytesPerSec,
        totalBytes: clusters * spc * bytesPerSec,
      };
    } finally { ff.free(pfs); ff.free(nclst); }
  }
}

// Suggest a FAT-8.3-safe name (codepage 936) unique within existingNames.
// CJK chars count as 2 GBK bytes. Returns hostName unchanged when it already
// fits; FatFs's own FR_INVALID_NAME remains the final arbiter on write.
export function suggestName(existingNames, hostName) {
  const clip = (s, max) => {
    let out = '';
    for (const c of s) { if (gbkLen(out + c) > max) break; out += c; }
    return out;
  };
  const bad = new RegExp(FAT_BAD.source, 'g');
  const dot = hostName.lastIndexOf('.');
  // Split at the LAST dot only; any dot still inside the base ('archive.tar.gz',
  // leading-dot '.gitignore' which is all-base) becomes '_' — a dotted base has
  // no 8.3 form and ensure83 would reject it, defeating WYSIWYG via LFN.
  let base = (dot > 0 ? hostName.slice(0, dot) : hostName)
    .replace(bad, '_').replace(/\./g, '_');
  const ext = (dot > 0 ? hostName.slice(dot + 1) : '').replace(bad, '_');
  base = clip(base, 8) || '_';
  const ext3 = clip(ext, 3);
  const taken = new Set(existingNames.map((n) => n.toUpperCase()));
  let cand = ext3 ? `${base}.${ext3}` : base;
  for (let i = 1; taken.has(cand.toUpperCase()); i++) {
    const suffix = `~${i}`;
    cand = clip(base, 8 - suffix.length) + suffix + (ext3 ? `.${ext3}` : '');
  }
  return cand;
}
